import { useState, useEffect, useCallback, useRef } from 'react';

export interface SubscriptionData {
  plan_tier: 'free' | 'pro' | 'unlimited';
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'free';
  client_limit: number;
  ai_enabled: boolean;
  client_billing_enabled: boolean;
  trial_end: string | null;
  trial_days_left: number;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface SubscriptionState {
  subscription: SubscriptionData | null;
  clientCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  isGated: boolean;
  canUseAI: boolean;
  isAtClientLimit: boolean;
  canBillClients: boolean;
}

export const DEFAULT_FREE_CLIENT_LIMIT = 10;

const DEFAULT_SUB: SubscriptionData = {
  plan_tier: 'free',
  status: 'free',
  client_limit: DEFAULT_FREE_CLIENT_LIMIT,
  ai_enabled: false,
  client_billing_enabled: false,
  trial_end: null,
  trial_days_left: 0,
  cancel_at_period_end: false,
  current_period_end: null,
  stripe_customer_id: null,
  stripe_subscription_id: null,
};

const REFETCH_COOLDOWN_MS = 5 * 60 * 1000; // only re-fetch on focus every 5 min

export function useSubscription(enabled = true): SubscriptionState {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [clientCount, setClientCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const lastFetchRef = useRef(0);

  const fetchStatus = useCallback(async (background = false) => {
    try {
      if (!background) setLoading(true);
      setError(null);

      let res = await fetch('/api/billing/status', { credentials: 'include' });

      // Retry once on 401/403 — dual-session cookie race can cause transient failures
      if ((res.status === 401 || res.status === 403) && !background) {
        await new Promise((r) => setTimeout(r, 1000));
        res = await fetch('/api/billing/status', { credentials: 'include' });
      }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setSubscription(null);
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json.ok) {
        setSubscription(json.subscription);
        setClientCount(json.client_count ?? 0);
        lastFetchRef.current = Date.now();
      } else {
        throw new Error(json.message || 'Failed to load subscription');
      }
    } catch (err: any) {
      console.error('[useSubscription] fetch error:', err);
      setError(err?.message || 'Failed to load subscription');
      setSubscription((prev) => prev ?? DEFAULT_SUB);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchStatus();
    }
  }, [enabled, fetchStatus]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => {
      if (Date.now() - lastFetchRef.current > REFETCH_COOLDOWN_MS) {
        fetchStatus(true);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, fetchStatus]);

  const sub = subscription || DEFAULT_SUB;

  const isGated = sub.status === 'past_due' || sub.status === 'canceled' || sub.status === 'unpaid';

  const canUseAI = sub.ai_enabled && !isGated;

  const isAtClientLimit = clientCount >= sub.client_limit;

  const canBillClients = sub.client_billing_enabled && !isGated;

  return {
    subscription,
    clientCount,
    loading,
    error,
    refresh: () => fetchStatus(false),
    isGated,
    canUseAI,
    isAtClientLimit,
    canBillClients,
  };
}
